import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronUp,
  ChevronDown,
  Navigation,
  Phone,
  MessageCircle,
  Loader2,
  LucideIcon,
} from 'lucide-react';

interface TripBottomSheetProps {
  title: string;
  subtitle: string;
  statusColor: string;
  StatusIcon: LucideIcon;
  primaryAction: string;
  onPrimaryAction: () => void;
  onStartNavigation?: () => void;
  navLoading?: boolean;
  navigationLabel?: string;
  pickupName?: string;
  pickupPhone?: string;
  customerName: string;
  customerPhone: string;
  onOpenChat?: () => void;
  defaultExpanded?: boolean;
}

export function TripBottomSheet({
  title,
  subtitle,
  statusColor,
  StatusIcon,
  primaryAction,
  onPrimaryAction,
  onStartNavigation,
  navLoading = false,
  navigationLabel = 'Open Google Maps',
  pickupName,
  pickupPhone,
  customerName,
  customerPhone,
  onOpenChat,
  defaultExpanded = false,
}: TripBottomSheetProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <motion.div
      className="absolute left-0 right-0 bottom-0 z-40 pointer-events-none"
      style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 320, damping: 28 }}
    >
      <div className="pointer-events-auto mx-3 mb-3">
        <motion.div
          layout
          className="bg-white rounded-[20px] shadow-2xl border border-gray-100 overflow-hidden"
        >
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="w-full px-4 pt-3 pb-2 text-left touch-manipulation"
          >
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-3" />
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${statusColor}18` }}
              >
                <StatusIcon className="w-5 h-5" style={{ color: statusColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold text-[#111111]">{title}</p>
                <p className="text-[12px] text-[#6B6B6B] truncate">{subtitle}</p>
              </div>
              {expanded ? (
                <ChevronDown className="w-5 h-5 text-[#6B6B6B] shrink-0" />
              ) : (
                <ChevronUp className="w-5 h-5 text-[#6B6B6B] shrink-0" />
              )}
            </div>
          </button>

          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-3 border-t border-gray-100 pt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-[#6B6B6B] uppercase">Pickup contact</p>
                      <p className="text-[14px] text-[#111111]">{pickupName || 'Pickup'}</p>
                      <p className="text-[12px] text-[#6B6B6B]">{pickupPhone || 'No phone'}</p>
                    </div>
                    {pickupPhone ? (
                      <a
                        href={`tel:${pickupPhone}`}
                        className="w-10 h-10 rounded-full bg-[#E0001B]/10 flex items-center justify-center"
                      >
                        <Phone className="w-4 h-4 text-[#E0001B]" />
                      </a>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-[#6B6B6B] uppercase">Delivery contact</p>
                      <p className="text-[14px] text-[#111111]">{customerName}</p>
                      <p className="text-[12px] text-[#6B6B6B]">{customerPhone || 'No phone'}</p>
                    </div>
                    <div className="flex gap-2">
                      {customerPhone ? (
                        <a
                          href={`tel:${customerPhone}`}
                          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"
                        >
                          <Phone className="w-4 h-4 text-[#111111]" />
                        </a>
                      ) : null}
                      {onOpenChat && (
                        <button
                          type="button"
                          onClick={onOpenChat}
                          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"
                        >
                          <MessageCircle className="w-4 h-4 text-[#111111]" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="p-4 pt-2 space-y-2 border-t border-gray-50">
            <motion.button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPrimaryAction();
              }}
              className="w-full h-12 rounded-[14px] text-white font-semibold flex items-center justify-center gap-2 shadow-md touch-manipulation"
              style={{ backgroundColor: statusColor }}
              whileTap={{ scale: 0.98 }}
            >
              <StatusIcon className="w-5 h-5" />
              {primaryAction}
            </motion.button>

            {onStartNavigation && (
              <motion.button
                type="button"
                onClick={onStartNavigation}
                disabled={navLoading}
                className="w-full h-11 rounded-[14px] bg-[#111111] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60 touch-manipulation"
                whileTap={{ scale: 0.98 }}
              >
                {navLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Navigation className="w-4 h-4" />
                )}
                {navLoading ? 'Starting navigation...' : navigationLabel}
              </motion.button>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
